import { useContext, useEffect, useState } from "react";
import {
  AdditionalDocumentForm,
  AdditionalDocumentFormValues,
  AlertComponent,
  DialogComponent,
  ModalComponent,
} from "../../../components";
import { Button } from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { useRequest } from "../../../hooks";
import {
  DeleteAction,
  DownloadAction,
  EditAction,
  OpenAction,
} from "../../../components/shared/DocRowActions";
import { usePaginator } from "../../../hooks/usePaginator";
import { ClientProps, DocRow } from "../../../interfaces/interfaces";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";
import DataContext from "../../../context/DataContext";

const isSuccessful = (response: any) => response?.success || response?.succes;

const toDateInput = (value?: string) =>
  value ? new Date(value).toISOString().split("T")[0] : "";

const fmtDate = (d: string) => {
  if (!d) return "—";
  const dt = new Date(d.includes("T") ? d : d + "T00:00:00");
  if (isNaN(dt.getTime())) return "—";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

export const AdditionalDocumentsAdmin = () => {
  const { handleRequest } = useRequest();
  const { clientOptions, fetchDataClient } = useContext(DataContext);
  const { paginationModel, setPaginationModel } = usePaginator("additional-documents-admin", 8);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<DocRow | null>(null);
  const [rowToDelete, setRowToDelete] = useState<DocRow | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  // Filter/search state — previously owned internally by DocumentsPortalLayout,
  // now lifted up because ListPageLayout is a controlled component: it renders
  // whatever `rows` slice it's given and expects the caller to own filtering,
  // search and pagination.
  const [docSearch, setDocSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => { fetchDataClient(); }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSuccess(false);
      setIsError(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, [isSuccess, isError]);

  const fetchDocuments = (client: ClientProps | null) => {
    setSelectedClient(client);
    if (!client) {
      setRows([]);
      return;
    }
    setRowsLoading(true);
    handleRequest({
      endpoint: `additional-document-list/client/${client.id}`,
      options: { method: "GET" },
    })
      .then((response) => {
        const docs: DocRow[] = (isSuccessful(response) ? response.data : []).map(
          (item: any) => ({
            rowId: `ad-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellName: "",
            source: "uploaded" as const,
            path: item.path,
            owner: "—",
            mimeType: item.mimeType,
            originalFileName: item.originalFileName,
          }),
        );
        setRows(docs);
      })
      .catch(() => setIsError(true))
      .finally(() => setRowsLoading(false));
  };

  const downloadFile = async (row: DocRow, open = false) => {
    if (!row.path || isBusy) return;
    setIsBusy(true);
    try {
      const response = await handleRequest({
        endpoint: "additional-document-download",
        options: {
          method: "POST",
          body: JSON.stringify({ key: row.path }),
          headers: { responseType: "Buffer" } as any,
        },
      });
      if (!response.success) throw new Error("Download failed");
      const mimeType = response.mimeType || row.mimeType || "application/octet-stream";
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(response.data.data).buffer], { type: mimeType }),
      );
      if (open && mimeType.includes("pdf")) {
        window.open(url);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", row.originalFileName || row.customName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    } catch {
      setIsError(true);
    } finally {
      setIsBusy(false);
    }
  };

  const submitDocument = async (values: AdditionalDocumentFormValues) => {
    setIsBusy(true);
    setIsModalOpen(false);
    try {
      const formData = new FormData();
      formData.append("client", JSON.stringify(values.client));
      formData.append("customName", values.customName);
      values.date && formData.append("installationDate", values.date);
      values.file && formData.append("file", values.file, values.file.name);
      formData.append("type", "additional-document");

      const endpoint = editingRow
        ? `${process.env.REACT_APP_DEV_API}/additional-document/${editingRow.rowId.replace("ad-", "")}`
        : `${process.env.REACT_APP_DEV_API}/additional-document-upload`;
      const response = await fetch(endpoint, {
        method: editingRow ? "PUT" : "POST",
        body: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem("info")}` },
      });
      const result = await response.json();
      if (!result.success) throw new Error("Save failed");
      setIsSuccess(true);
      fetchDocuments(values.client);
      setEditingRow(null);
    } catch {
      setIsError(true);
    } finally {
      setIsBusy(false);
    }
  };

  const deleteDocument = () => {
    if (!rowToDelete) return;
    setIsBusy(true);
    handleRequest({
      endpoint: `additional-document/${rowToDelete.rowId.replace("ad-", "")}`,
      options: { method: "DELETE" },
    })
      .then((response) => {
        if (!response.success) throw new Error("Delete failed");
        setIsSuccess(true);
        fetchDocuments(selectedClient);
      })
      .catch(() => setIsError(true))
      .finally(() => {
        setIsBusy(false);
        setRowToDelete(null);
      });
  };

  // ---- Derived: search -> date filters ----
  let filtered = docSearch.trim()
    ? rows.filter((r) => r.customName.toLowerCase().includes(docSearch.trim().toLowerCase()))
    : rows;
  if (dateFrom || dateTo) {
    filtered = filtered.filter((r) => {
      const d = new Date(r.date + "T12:00:00");
      if (dateFrom && d < new Date(dateFrom + "T00:00:00")) return false;
      if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
      return true;
    });
  }

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, totalPages - 1);
  const pageRows = filtered.slice(
    currentPage * paginationModel.pageSize,
    (currentPage + 1) * paginationModel.pageSize,
  );

  const columns: ListColumn<DocRow>[] = [
    { key: "customName", header: "Document", render: (r) => r.customName, width: "2fr" },
    { key: "date", header: "Date", render: (r) => fmtDate(r.date), width: "120px" },
  ];

  const filters: ListFilterField[] = [
    { kind: "date", key: "dateFrom", label: "From", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To", value: dateTo, onChange: setDateTo },
  ];

  return (
    <>
      <ListPageLayout<DocRow>
        title="Additional Documents"
        subtitle="Browse, filter and download client-level documents"
        columns={columns}
        rows={pageRows}
        getRowId={(r) => r.rowId}
        rowsLoading={rowsLoading || isBusy}
        isError={isError}
        emptyTitle={
          !selectedClient ? "Select a client to load additional documents" : undefined
        }
        searchValue={docSearch}
        onSearchChange={setDocSearch}
        searchPlaceholder="Search additional documents…"
        filters={filters}
        page={currentPage}
        pageSize={paginationModel.pageSize}
        totalCount={totalCount}
        onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
        onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
        rowsPerPageOptions={[8, 15, 25, 50]}
        toolbarActions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {clientOptions?.length ? (
              <select
                value={selectedClient ? String(selectedClient.id) : ""}
                onChange={(e) => {
                  const c =
                    clientOptions.find((cl) => String(cl.id) === e.target.value) ?? null;
                  fetchDocuments(c);
                }}
              >
                <option value="" disabled>Select a client...</option>
                {clientOptions.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            ) : null}
            <Button
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => {
                setEditingRow(null);
                setIsModalOpen(true);
              }}
            >
              Upload
            </Button>
          </div>
        }
        rowActions={(r) => (
          <>
            <OpenAction rowId={r.rowId} onClick={() => downloadFile(r, true)} />
            <DownloadAction rowId={r.rowId} busy={isBusy} onClick={() => downloadFile(r)} />
            <DeleteAction rowId={r.rowId} onClick={() => setRowToDelete(r)} />
            <EditAction
              rowId={r.rowId}
              onClick={() => {
                setEditingRow(r);
                setIsModalOpen(true);
              }}
            />
          </>
        )}
      />

      {isSuccess && <AlertComponent type="success" />}
      {isModalOpen && (
        <ModalComponent
          modalContent={
            <AdditionalDocumentForm
              clientOptions={clientOptions}
              initialValues={
                editingRow
                  ? {
                      client: selectedClient ?? undefined,
                      customName: editingRow.customName,
                      date: toDateInput(editingRow.date),
                    }
                  : { client: selectedClient ?? undefined }
              }
              requireFile={!editingRow}
              onSubmit={submitDocument}
              onCancel={() => {
                setIsModalOpen(false);
                setEditingRow(null);
              }}
            />
          }
        />
      )}
      {rowToDelete && (
        <DialogComponent
          type="error"
          modalContent={
            <div>Are you sure you want to remove this document?</div>
          }
          onAccept={deleteDocument}
          onCancel={() => setRowToDelete(null)}
        />
      )}
    </>
  );
};
